import { roundGridSpans, fullWidthRoundSections } from "./roundGridLayout";
import { HomeRoundState } from "./HomeRoundState";
import { RoundEntryLink, RoundOpeningProvider } from "./RoundEntryLink";
import { isApplicationSourceRevoked } from "@client/offline/artifactQueryPersistence";
import { APPLICATION_AVAILABILITY_QUERY_OPTIONS } from "@client/components/applications/applicationAvailability";
import { OpenFundingOpportunities } from "@client/components/applications/OpenFundingOpportunities";
import {
  useRoundEntryHref,
  RoundEntryLinksProvider,
} from "@client/hooks/use-round-entry-href";
import { Link } from "wouter";
import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { downloadRoundScopedFile } from "@client/lib/downloadRoundScopedFile";
import { CRITICAL_TRPC_OPTIONS } from "@client/trpcLinks";
import { RouteScroller } from "@client/components/utils/RouteScroller";
import { useFeatureFlags } from "@client/hooks/use-feature-flag-enabled";
import { HomeApplications } from "./HomeTeams";
import { ApplicationLoading } from "@client/components/applications/ApplicationLoading";
import { RoundWizard } from "@client/components/wizard/RoundWizard";
import { useCurrentUser } from "@client/hooks/use-current-user";
import { useImpersonationRefValue } from "@client/ImperativeState";
import {
  isIPadOffline,
  isIPadOfflineFailure,
  OFFLINE_CACHE_KEYS,
  readOfflineCache,
  writeOfflineCacheIfIPad,
} from "@client/offline/offlineCache";
import { trpc } from "@client/trpc";
import { groupVisibleRounds } from "@lib/roundGroups";
import { isDemoRoundName } from "@lib/constants";
import {
  Text,
  Button,
  Card,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogTrigger,
  Input,
  useLocalStorage,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@sfc/ui";
import { ArrowRight, ChevronDownIcon, DownloadIcon } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Onboarding } from "../../components/wizard/Onboarding";
import { resolveHomepageView } from "./resolveHomepageView";
import { RoundActionButton } from "./RoundActionButton";

const DownloadRoundButton = withErrorBoundary(function DownloadRoundButton({
  roundId,
  roundName,
}: {
  roundId: string;
  roundName: string;
}) {
  const [downloading, setDownloading] = useState(false);

  async function handleDownload(e: React.MouseEvent) {
    e.preventDefault();
    setDownloading(true);

    try {
      const result = await downloadRoundScopedFile({
        path: `/api/download-round?roundId=${encodeURIComponent(roundId)}`,
        filename: `${roundName}_database.json`,
      });

      if (!result.ok) {
        toast.error(result.error);
      }
    } finally {
      setDownloading(false);
    }
  }

  return (
    <RoundActionButton isLoading={downloading} onClick={handleDownload}>
      <DownloadIcon />
      Download
    </RoundActionButton>
  );
});

// Admin-only quick links into a round's application, proposal, and review pages.
function RoundAdminShortcuts({
  roundId,
  hasApplications,
}: {
  roundId: string;
  hasApplications: boolean;
}) {
  const roundEntryHref = useRoundEntryHref();
  return (
    <>
      {hasApplications && (
        <RoundActionButton
          roundId={roundId}
          href={roundEntryHref(`/round/${roundId}/applications/responses`)}
        >
          Applications
        </RoundActionButton>
      )}
      <RoundActionButton
        roundId={roundId}
        href={roundEntryHref(`/round/${roundId}/proposals`)}
      >
        Proposals
      </RoundActionButton>
      <RoundActionButton
        roundId={roundId}
        href={roundEntryHref(`/round/${roundId}/funder/review`)}
      >
        Review
      </RoundActionButton>
    </>
  );
}

// Shared round destination for the main directory and collapsed admin/archive lists.
function RoundListRow({
  roundId,
  roundName,
  archivedAt,
  inset = true,
  stateViewerId,
  nonParticipatingAdmin = false,
  grouped = false,
  compact = false,
  gridClassName = "",
  horizontal = false,
  showAdminShortcuts,
  hasApplications = false,
  showDownload,
}: {
  roundId: string;
  roundName: string;
  archivedAt?: Date | string | null;
  inset?: boolean;
  stateViewerId?: string;
  nonParticipatingAdmin?: boolean;
  grouped?: boolean;
  compact?: boolean;
  gridClassName?: string;
  horizontal?: boolean;
  showAdminShortcuts: boolean;
  hasApplications?: boolean;
  showDownload: boolean;
}) {
  const roundEntryHref = useRoundEntryHref();
  const isDemo = isDemoRoundName(roundName);
  return (
    <li
      className={
        compact
          ? "group min-w-0 border-b border-border py-2 last:border-b-0"
          : `group min-w-0 space-y-1 pb-2 ${horizontal ? "sm:grid sm:grid-cols-[minmax(0,1fr)_fit-content(40%)] sm:items-center sm:gap-8 sm:space-y-0" : ""} ${gridClassName} ${grouped ? "flex flex-col gap-2 space-y-0 border-l-2 border-border pl-4" : "pt-5"} ${inset ? "px-5" : ""}`
      }
    >
      <div className={`min-w-0 space-y-1`}>
        {!compact &&
          (isDemo ? (
            <span className="inline-flex rounded-md border border-dashed border-border px-2 py-1 label-sm text-muted-foreground">
              {archivedAt != null ? "Archived demo" : "Demo round"}
            </span>
          ) : !grouped ? (
            <Text
              tone="muted-foreground"
              className="label-sm uppercase tracking-wide"
            >
              {archivedAt != null ? "Archived round" : "Funding round"}
            </Text>
          ) : null)}
        <h3
          className={`font-sans ${compact ? "body-md font-medium text-foreground" : grouped ? "text-heading-sm font-semibold text-primary" : isDemo ? "text-heading-md font-semibold text-foreground" : "text-heading-lg font-semibold text-primary"}`}
        >
          <RoundEntryLink
            roundId={roundId}
            href={roundEntryHref(`/round/${roundId}/`)}
            aria-label={`Open round: ${roundName}`}
            className={`inline-flex pointer-coarse:min-h-11 max-w-full items-center gap-3 hover:underline underline-offset-4 focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring`}
          >
            <span className="min-w-0 [overflow-wrap:anywhere]">
              {roundName}
            </span>
            <ArrowRight
              className="size-4 shrink-0 group-data-[pending=true]/round-entry:hidden"
              aria-hidden="true"
            />
          </RoundEntryLink>
        </h3>
        {stateViewerId && (
          <HomeRoundState roundId={roundId} viewerId={stateViewerId} />
        )}
      </div>
      <div
        className={`min-w-0 space-y-1 ${grouped && !compact ? "mt-auto pt-2" : ""}`}
      >
        {!stateViewerId &&
          !showAdminShortcuts &&
          !nonParticipatingAdmin &&
          !grouped &&
          !compact && (
            <Text
              variant="body-md"
              tone="muted-foreground"
              className="max-w-[32ch] leading-relaxed"
            >
              {archivedAt != null
                ? `Archived ${new Date(archivedAt).toLocaleDateString(undefined, { dateStyle: "medium" })}. Open the round to view its records.`
                : isDemo
                  ? "Explore a demonstration of the round workspace."
                  : "Explore the requests and information in this round."}
            </Text>
          )}
        {(showAdminShortcuts || showDownload) && (
          <div
            className={`home-round-shortcuts flex max-w-full flex-wrap items-center gap-x-3 gap-y-1 ${horizontal ? "sm:justify-end" : ""}`}
          >
            {showAdminShortcuts && (
              <RoundAdminShortcuts
                roundId={roundId}
                hasApplications={hasApplications}
              />
            )}
            {showDownload && (
              <DownloadRoundButton roundId={roundId} roundName={roundName} />
            )}
          </div>
        )}
      </div>
    </li>
  );
}

function AdminDemoRoundTool() {
  const roundEntryHref = useRoundEntryHref();
  const utils = trpc.useUtils();
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [needsName, setNeedsName] = useState(false);
  const [lastCreatedRound, setLastCreatedRound] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const createDemoRound = trpc.createDemoRound.useMutation({
    onSuccess: async (result) => {
      if (result.status === "needsName") {
        setNeedsName(true);
        toast.error("Enter a name for this demo user and submit again.");
        return;
      }

      setLastCreatedRound({
        id: result.roundId,
        name: result.roundName,
      });
      setEmail("");
      setName("");
      setNeedsName(false);
      toast.success(`Created ${result.roundName}`);
      await utils.getHomepageData.invalidate();
    },
    onError: (error) => {
      toast.error(error.message || "Failed to create demo round");
    },
  });

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();

    const normalizedEmail = email.trim();
    const normalizedName = name.trim();

    if (normalizedEmail.length === 0) {
      toast.error("Email is required");
      return;
    }

    createDemoRound.mutate({
      email: normalizedEmail,
      name: needsName ? normalizedName || undefined : undefined,
    });
  }

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="p-0 overflow-hidden">
        <CollapsibleTrigger
          render={
            <button
              type="button"
              className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left hover:bg-accent"
            />
          }
        >
          <div>
            <h2 className="text-sm font-medium">Admin demo round tool</h2>
            <Text size="xs" tone="muted-foreground">
              Manually create a demo round for a user.
            </Text>
          </div>
          <ChevronDownIcon
            className={`size-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </CollapsibleTrigger>

        <CollapsibleContent>
          <form
            className="border-t px-4 py-4 flex flex-col gap-3"
            onSubmit={handleSubmit}
          >
            <div className="flex flex-col gap-2">
              <label htmlFor="demo-round-email" className="text-sm font-medium">
                User Email
              </label>
              <Text size="xs" tone="muted-foreground">
                Enter the email for the user you are creating this demo round
                for.
              </Text>
              <Input
                id="demo-round-email"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  setNeedsName(false);
                }}
                placeholder="user@example.com"
                required
              />
            </div>

            {needsName && (
              <div className="flex flex-col gap-2">
                <label
                  htmlFor="demo-round-name"
                  className="text-sm font-medium"
                >
                  Name
                </label>
                <Input
                  id="demo-round-name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="User Name"
                  required
                />
              </div>
            )}

            <div className="flex items-center gap-3">
              <Button type="submit" isLoading={createDemoRound.isPending}>
                Create Test Round
              </Button>
              {lastCreatedRound && (
                <a
                  href={roundEntryHref(`/round/${lastCreatedRound.id}/`)}
                  className="text-sm underline"
                >
                  Open {lastCreatedRound.name}
                </a>
              )}
            </div>
          </form>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Archived rounds from both lists, collapsed at the bottom of the page. They
// stay reachable (an archived round is read-only, not gone) without crowding
// the rounds people are actually working in. Restoring happens inside the
// round, in Round Settings, so there is one restore path.
function ArchivedRounds({
  rounds,
  applicationRoundIds,
  isSfcAdmin,
}: {
  rounds: { id: string; name: string; archivedAt: Date | string | null }[];
  applicationRoundIds: string[];
  isSfcAdmin: boolean;
}) {
  const [open, setOpen] = useLocalStorage<boolean>(
    "homepageShowArchivedRounds",
    false,
  );

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <Card className="py-0 overflow-hidden">
        <CollapsibleTrigger className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left">
          <div>
            <h2 className="text-sm font-medium">
              Archived Rounds ({rounds.length})
            </h2>
            <Text size="xs" tone="muted-foreground">
              Read-only until restored from their Round Settings.
            </Text>
          </div>
          <ChevronDownIcon
            className={`size-4 shrink-0 text-muted-foreground transition-transform ${
              open ? "rotate-180" : ""
            }`}
          />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <ul className="border-t">
            {rounds.map((round) => (
              <RoundListRow
                showDownload={false}
                key={round.id}
                roundId={round.id}
                roundName={round.name}
                hasApplications={applicationRoundIds.includes(round.id)}
                archivedAt={round.archivedAt}
                showAdminShortcuts={isSfcAdmin}
              />
            ))}
          </ul>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
}

// Demo rounds are already grouped under the admin disclosure.
function DemoRounds({
  offline,
  rounds,
  applicationRoundIds,
}: {
  offline: boolean;
  rounds: { id: string; name: string }[];
  applicationRoundIds: string[];
}) {
  if (rounds.length === 0) {
    return null;
  }

  return (
    <section className="space-y-3" aria-label="Demo Rounds">
      <h3 className="heading-sm">Demo Rounds</h3>
      <ul className="border-y border-border">
        {rounds.map((round) => (
          <RoundListRow
            compact
            showDownload={!offline}
            key={round.id}
            roundId={round.id}
            roundName={round.name}
            hasApplications={applicationRoundIds.includes(round.id)}
            showAdminShortcuts={true}
          />
        ))}
      </ul>
    </section>
  );
}

export function Home() {
  return (
    <RoundEntryLinksProvider>
      <RoundOpeningProvider>
        <HomeContent />
      </RoundOpeningProvider>
    </RoundEntryLinksProvider>
  );
}

function HomeContent() {
  const [homeView, setHomeView] = useState("overview");
  const featureFlags = useFeatureFlags();
  const teamsEnabled = featureFlags.data?.teams;
  const query = trpc.getHomepageData.useQuery();
  type HomepageData = NonNullable<typeof query.data>;

  // getHomepageData is anonymized per the *effective* user (ctx.getUser()), so
  // its offline cache must be scoped to that user — otherwise an admin who
  // cached the page while impersonating could be served that user's round list
  // offline. The effective user is the impersonated user when impersonating,
  // else the underlying logged-in user (offline-stable via useCurrentUser).
  const { data: currentUser } = useCurrentUser();
  const impersonation = useImpersonationRefValue();
  const effectiveUserId = impersonation ?? currentUser?.userId ?? null;
  const roundGroupsQuery = trpc.getRoundGroups.useQuery(
    { viewerId: effectiveUserId ?? "" },
    { enabled: effectiveUserId != null },
  );
  const groupFailure = roundGroupsQuery.error ?? roundGroupsQuery.failureReason;
  const roundGroups =
    groupFailure && !isIPadOfflineFailure(groupFailure)
      ? []
      : (roundGroupsQuery.data ?? []);

  // Mirror the homepage into the iPad offline cache on success, and serve it
  // back when offline so a funder can still navigate to a cached round. Only
  // write once the query has refetched for the CURRENT effective user:
  // getHomepageData reuses the same React Query key across an impersonation
  // switch (the viewer rides in a header), so right after a switch `query.data`
  // still holds the previous viewer's round list. `query.data.user.id` is the
  // viewer the payload is for — gating on it prevents writing stale data under
  // the new user's key (which they could then see offline).
  useEffect(() => {
    if (
      query.data?.user?.id != null &&
      query.data.user.id === effectiveUserId
    ) {
      writeOfflineCacheIfIPad(
        OFFLINE_CACHE_KEYS.homepageData(effectiveUserId),
        query.data,
      );
    }
  }, [query.data, effectiveUserId]);

  // `error ?? failureReason`, because a refetch resets a dataless errored
  // query to pending with `error` cleared (SP-2612) — `failureReason` still
  // carries the in-flight attempt's transport error, so the cached round list
  // keeps rendering instead of flapping back to the loading skeleton.
  const queryFailure = query.error ?? query.failureReason;
  const offline =
    isIPadOffline() ||
    (queryFailure != null && isIPadOfflineFailure(queryFailure));
  const discoveryEnabled = teamsEnabled === true;
  const openApplications = trpc.getOpenApplications.useQuery(undefined, {
    ...APPLICATION_AVAILABILITY_QUERY_OPTIONS,
    ...CRITICAL_TRPC_OPTIONS,
    enabled: !offline && effectiveUserId != null && discoveryEnabled,
    retry: false,
    gcTime: 0,
  });
  const canLoadMyApplications =
    !offline &&
    discoveryEnabled &&
    effectiveUserId != null &&
    query.data?.user?.id === effectiveUserId;
  const myApplications = trpc.getMyApplications.useQuery(undefined, {
    ...APPLICATION_AVAILABILITY_QUERY_OPTIONS,
    enabled: canLoadMyApplications,
    gcTime: 0,
  });
  const ownedApplications =
    !isApplicationSourceRevoked(myApplications.error) &&
    myApplications.data?.viewerId === effectiveUserId
      ? myApplications.data.applications
      : undefined;
  const startedRoundIds = new Set(
    ownedApplications?.map((application) => application.roundId),
  );
  const applications =
    discoveryEnabled &&
    !isApplicationSourceRevoked(openApplications.error) &&
    openApplications.data?.viewerId === effectiveUserId
      ? openApplications.data.applications.filter(
          (application) => !startedRoundIds.has(application.roundId),
        )
      : [];
  const applicationsLoading =
    !offline &&
    effectiveUserId != null &&
    (featureFlags.isPending ||
      (discoveryEnabled && openApplications.isLoading) ||
      (canLoadMyApplications &&
        !myApplications.isError &&
        ownedApplications === undefined));
  const applicationHistoryError =
    canLoadMyApplications &&
    myApplications.isError &&
    ownedApplications === undefined;
  const applicationsError =
    !offline &&
    discoveryEnabled &&
    ((openApplications.isError &&
      (isApplicationSourceRevoked(openApplications.error) ||
        openApplications.data?.viewerId !== effectiveUserId)) ||
      applicationHistoryError);
  const applicationList =
    effectiveUserId &&
    (applicationsError ? (
      <div role="alert" className="flex flex-col items-start gap-2">
        <p>
          {applicationHistoryError
            ? "Couldn’t check which rounds you’ve applied to."
            : "Couldn’t load open funding rounds."}
        </p>
        <Button
          variant="nav"
          onClick={() =>
            void (applicationHistoryError
              ? myApplications.refetch()
              : openApplications.refetch())
          }
        >
          Try again
        </Button>
      </div>
    ) : applicationsLoading ? (
      <ApplicationLoading label="Loading applications" rows={1} />
    ) : applications.length > 0 ? (
      <OpenFundingOpportunities applications={applications} />
    ) : null);

  const applicationRounds = trpc.getHomepageApplicationRoundIds.useQuery(
    undefined,
    {
      enabled:
        !offline &&
        query.data?.user?.id === effectiveUserId &&
        query.data?.user?.isSfcAdmin === true,
    },
  );
  const applicationRoundIds =
    applicationRounds.data?.viewerId === effectiveUserId
      ? applicationRounds.data.roundIds
      : [];
  const cachedData =
    offline && effectiveUserId != null
      ? readOfflineCache<HomepageData>(
          OFFLINE_CACHE_KEYS.homepageData(effectiveUserId),
        )
      : null;
  // Scope the rendered data to the current effective user (never expose another
  // viewer's payload from the shared query key), prefer the offline cache when
  // the live data isn't usable, and resolve loading/error without spinning
  // forever on a failed post-impersonation refetch. Pure + unit-tested.
  const { data, isLoading, isError } = resolveHomepageView<HomepageData>({
    queryData: query.data,
    cachedData,
    effectiveUserId,
    isQueryLoading: query.isLoading,
    isQueryError: query.isError,
  });

  const demoSetup = trpc.getHomepageDemoRound.useQuery(undefined, {
    ...CRITICAL_TRPC_OPTIONS,
    enabled:
      !offline &&
      !!effectiveUserId &&
      !impersonation &&
      data?.user?.id === effectiveUserId,
    gcTime: 0,
  });
  const recoveredDemo =
    !offline && demoSetup.data?.viewerId === effectiveUserId
      ? demoSetup.data.membership
      : null;
  const participatingIn = [...(data?.user?.participating_in ?? [])];

  if (
    recoveredDemo &&
    !participatingIn.some((entry) => entry.round.id === recoveredDemo.round.id)
  ) {
    participatingIn.push(recoveredDemo);
  }

  const visibleRounds = [
    ...participatingIn,
    ...(data?.nonParticipatingRounds ?? []).map((round) => ({
      round,
      track: "NO_TRACK",
    })),
  ];
  const completedRoundIds = new Set(data?.completedRoundIds ?? []);
  const pastRounds = visibleRounds
    .map(({ round }) => round)
    .filter(
      (round) => round.archivedAt == null && completedRoundIds.has(round.id),
    );

  // Past and archived rounds leave the active directory.
  const liveParticipatingIn = participatingIn.filter(
    ({ round }) => round.archivedAt == null && !completedRoundIds.has(round.id),
  );
  const liveNonParticipating = (data?.nonParticipatingRounds ?? []).filter(
    (round) => round.archivedAt == null && !completedRoundIds.has(round.id),
  );
  const homeRounds = [
    ...liveParticipatingIn,
    ...(data?.user?.isSfcAdmin
      ? liveNonParticipating
          .filter(
            (round) =>
              !isDemoRoundName(round.name) &&
              !liveParticipatingIn.some((entry) => entry.round.id === round.id),
          )
          .map((round) => ({ round, track: "NO_TRACK" }))
      : []),
  ];
  // Keep groups intact, with demo-only groups last and demos last within a group.
  const homeSections = groupVisibleRounds(homeRounds, roundGroups)
    .map((section) => ({
      ...section,
      entries: section.entries.toSorted(
        (a, b) =>
          Number(isDemoRoundName(a.round.name)) -
          Number(isDemoRoundName(b.round.name)),
      ),
    }))
    .sort(
      (a, b) =>
        Number(a.entries.every(({ round }) => isDemoRoundName(round.name))) -
        Number(b.entries.every(({ round }) => isDemoRoundName(round.name))),
    );
  const fullWidthSections = fullWidthRoundSections(homeSections);
  const archivedRounds = visibleRounds
    .map(({ round }) => round)
    .filter((round) => round.archivedAt != null);

  if (
    teamsEnabled === false &&
    !offline &&
    !isError &&
    !isLoading &&
    !applicationsLoading &&
    visibleRounds.length === 0
  ) {
    return (
      <Onboarding
        isSfcAdmin={data?.user?.isSfcAdmin ?? false}
        before={
          <div className="mx-auto w-full max-w-4xl space-y-6 px-4 pt-6 md:px-8">
            {applicationList}
            {data?.user?.isSfcAdmin && <AdminDemoRoundTool />}
          </div>
        }
      />
    );
  }

  const createRoundDialog = data?.user?.isSfcAdmin && !offline && (
    <Dialog>
      <DialogTrigger
        render={
          <Button
            variant="nav"
            className="self-start pointer-coarse:min-h-11"
          />
        }
      >
        Create Round
      </DialogTrigger>
      <DialogContent className="bg-background">
        <DialogTitle className="text-center">Create Round</DialogTitle>
        <RoundWizard isSfcAdmin={data.user.isSfcAdmin} />
      </DialogContent>
    </Dialog>
  );

  const canShowRounds = visibleRounds.length > 0 || !!data?.user?.isSfcAdmin;
  const canShowApplications = !offline && discoveryEnabled;
  const selectedView =
    (homeView === "applications" && !canShowApplications) ||
    (homeView === "reviews" && !canShowRounds) ||
    homeView === "admin"
      ? "overview"
      : homeView;

  const applicantContent = data?.user &&
    !offline &&
    !applicationHistoryError &&
    !applicationsLoading &&
    teamsEnabled !== undefined && (
      <HomeApplications
        viewerId={data.user.id}
        enabled={discoveryEnabled}
        showEmpty={
          selectedView === "applications" ||
          (!applicationsLoading &&
            !applicationsError &&
            visibleRounds.length === 0 &&
            applications.length === 0)
        }
      />
    );
  const archivedContent = archivedRounds.length > 0 && (
    <ArchivedRounds
      rounds={archivedRounds}
      applicationRoundIds={applicationRoundIds}
      isSfcAdmin={data?.user?.isSfcAdmin ?? false}
    />
  );
  const reviewContent = canShowRounds && (
    <section aria-label="Your Rounds" className="space-y-8">
      {homeRounds.length > 0 ? (
        <div>
          <ul className="grid grid-cols-1 items-start gap-x-8 gap-y-5 sm:grid-cols-2">
            {homeSections.map((section) => {
              const twoColumnSpans = roundGridSpans(section.entries.length, 2);
              const threeColumnSpans = roundGridSpans(
                section.entries.length,
                3,
              );
              const rows = section.entries.map(({ round, track }, index) => (
                <RoundListRow
                  inset={false}
                  grouped={section.name != null}
                  horizontal={
                    section.name == null && fullWidthSections.has(section.id)
                  }
                  gridClassName={
                    section.name == null
                      ? ""
                      : [
                          twoColumnSpans[index] === 6
                            ? "@min-[28rem]:col-span-6"
                            : "@min-[28rem]:col-span-3",
                          threeColumnSpans[index] === 6
                            ? "@min-[48rem]:col-span-6"
                            : threeColumnSpans[index] === 3
                              ? "@min-[48rem]:col-span-3"
                              : "@min-[48rem]:col-span-2",
                        ].join(" ")
                  }
                  stateViewerId={
                    !offline &&
                    !data?.user?.isSfcAdmin &&
                    participatingIn.some((entry) => entry.round.id === round.id)
                      ? (effectiveUserId ?? undefined)
                      : undefined
                  }
                  nonParticipatingAdmin={
                    !!data?.user?.isSfcAdmin &&
                    !participatingIn.some(
                      (entry) => entry.round.id === round.id,
                    )
                  }
                  showDownload={
                    !offline &&
                    (track === "NO_TRACK" || !!data?.user?.isSfcAdmin)
                  }
                  key={round.id}
                  roundId={round.id}
                  roundName={round.name}
                  hasApplications={applicationRoundIds.includes(round.id)}
                  showAdminShortcuts={data?.user?.isSfcAdmin ?? false}
                />
              ));

              return (
                <li
                  key={section.id}
                  className={`min-w-0 border-t-2 ${fullWidthSections.has(section.id) ? "sm:col-span-2" : ""} ${section.entries.every(({ round }) => isDemoRoundName(round.name)) ? "border-dashed border-border" : "border-primary/30"}`}
                >
                  {section.name == null ? (
                    <ul>{rows}</ul>
                  ) : (
                    <section aria-label={section.name} className="@container">
                      <div className="flex flex-wrap items-center justify-between gap-2 pt-5 pb-3">
                        <h3 className="label-sm uppercase tracking-wide text-muted-foreground">
                          {section.name}
                        </h3>
                        <span className="body-sm text-muted-foreground">
                          {section.entries.length}{" "}
                          {section.entries.length === 1 ? "round" : "rounds"}
                        </span>
                      </div>
                      <ul className="grid grid-cols-1 gap-6 pb-4 @min-[28rem]:grid-cols-6">
                        {rows}
                      </ul>
                    </section>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      ) : (
        <Text variant="body-md" tone="muted-foreground">
          {pastRounds.length > 0
            ? "You have no active rounds."
            : "You’re not participating in any rounds yet."}
        </Text>
      )}
      {pastRounds.length > 0 && (
        <details className="border-t border-border pt-4">
          <summary className="cursor-pointer body-md font-medium text-muted-foreground">
            Past rounds ({pastRounds.length})
          </summary>
          <ul className="mt-3 border-t border-border">
            {pastRounds.map((round) => (
              <RoundListRow
                key={round.id}
                compact
                roundId={round.id}
                roundName={round.name}
                showAdminShortcuts={data?.user?.isSfcAdmin ?? false}
                hasApplications={applicationRoundIds.includes(round.id)}
                showDownload={!offline && !!data?.user?.isSfcAdmin}
              />
            ))}
          </ul>
        </details>
      )}
      {data?.user?.isSfcAdmin && (
        <details className="space-y-4">
          <summary className="cursor-pointer body-md text-muted-foreground">
            Demo Rounds and Tools
          </summary>
          {createRoundDialog}
          <DemoRounds
            offline={offline}
            rounds={liveNonParticipating.filter((round) =>
              isDemoRoundName(round.name),
            )}
            applicationRoundIds={applicationRoundIds}
          />
          <AdminDemoRoundTool />
          {archivedContent}
        </details>
      )}
      {!data?.user?.isSfcAdmin && archivedContent}
    </section>
  );

  return (
    <RouteScroller>
      <main className="mx-auto w-full max-w-6xl px-4 py-10 md:px-8 lg:grid lg:grid-cols-[15rem_minmax(0,1fr)] lg:gap-12">
        <span hidden data-application-theme="green" data-home-design="warm" />
        <Tabs
          value={selectedView}
          onValueChange={(value) => setHomeView(String(value))}
          className="contents"
        >
          <header className="mb-8 flex flex-col items-start gap-6 lg:mb-0">
            <div className="space-y-2">
              <h1 className="application-title">Home</h1>
              <Text variant="body-md" tone="muted-foreground">
                Choose a round to get started.
              </Text>
            </div>
            {!offline && discoveryEnabled && (
              <Button
                nativeButton={false}
                variant="nav"
                className="max-w-full whitespace-normal px-0 text-left text-primary"
                render={<Link href="/apply" />}
              >
                Browse All Funding Rounds →
              </Button>
            )}
          </header>
          {/* Application discovery has its own loading boundary. A retry must not
              unmount the round list or reset its queries and expanded sections. */}
          {isLoading ? (
            <ApplicationLoading label="Loading home" />
          ) : isError || !data?.user ? (
            <Text tone="muted-foreground">
              An error occurred while loading the homepage. Please try
              refreshing the page.
            </Text>
          ) : (
            <div className="min-w-0 space-y-6">
              {!offline && applicationList}

              <div className="min-w-0 space-y-6">
                <TabsList className="w-full max-w-full gap-1 [&>button]:px-2 [&>button]:text-body-sm">
                  <TabsTrigger value="overview">Overview</TabsTrigger>
                  {canShowApplications && (
                    <TabsTrigger value="applications">Applications</TabsTrigger>
                  )}
                  {canShowRounds && (
                    <TabsTrigger value="reviews">Rounds</TabsTrigger>
                  )}
                </TabsList>
                <div className="min-w-0">
                  <TabsContent value="overview" className="space-y-8">
                    {applicantContent}
                    {reviewContent}
                  </TabsContent>
                  {canShowApplications && (
                    <TabsContent value="applications" className="space-y-8">
                      {applicantContent}
                    </TabsContent>
                  )}
                  {canShowRounds && (
                    <TabsContent value="reviews">{reviewContent}</TabsContent>
                  )}
                  {offline && (
                    <Text
                      variant="body-md"
                      tone="muted-foreground"
                      className="mt-6"
                    >
                      {visibleRounds.length === 0
                        ? "You’re offline. Connect to the internet to view your teams and available rounds."
                        : "You’re offline. Your saved rounds are available here."}
                    </Text>
                  )}
                  {!offline &&
                    !applicationsLoading &&
                    visibleRounds.length === 0 &&
                    teamsEnabled === undefined &&
                    (featureFlags.isError ? (
                      <div role="alert" className="mt-6 space-y-2">
                        <p>Couldn’t load your workspace.</p>
                        <Button
                          variant="nav"
                          onClick={() => void featureFlags.refetch()}
                        >
                          Try again
                        </Button>
                      </div>
                    ) : (
                      <ApplicationLoading label="Loading workspace" rows={1} />
                    ))}
                </div>
              </div>
            </div>
          )}
        </Tabs>
      </main>
    </RouteScroller>
  );
}
